// Auth.js
import React, { useState, useEffect } from 'react';
import { useUser } from './usersContext';
import { sendPasswordResetEmail } from 'firebase/auth';
import "./Auth.css";
import backgroundImage from './assets/images/backgroundtest.png';
import { auth } from './firebase';
import AdditionalInfoForm from './AdditionalInfoForm';
import { useLocation, useNavigate } from 'react-router-dom';
import api from './utils/api';
import googleLogo from './assets/Icons/google-logo.svg';

const subjectOptions = [
  "math", "physics", "chemistry", "biology", "french", "english", "spanish",
  "history", "philosophy", "coding", "music", "geography"
];

const AuthPage = () => {
  const {
    currentUser,
    userStatus,
    setUserStatus,
    isLoading,
    setIsLoading,
    signInWithGoogle,
    signInWithEmailPassword,
    signOutUser,
    resendVerificationEmail,
    statusMessage,
    showResendButton,
    showResetButton,
    setStatusMessage,
    setShowResendButton,
    setShowResetButton,
    setUserDoc,
    userDoc,
  } = useUser();

  const location = useLocation();
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [additionalInfo, setAdditionalInfo] = useState({
    firstName: '',
    lastName: '',
    phone: '',
    grade: '',
    userType: 'I am a...'
  });  const [subjects, setSubjects] = useState([]);
  const [showSubjectStep, setShowSubjectStep] = useState(false);
  const [profileCompletionError, setProfileCompletionError] = useState('');
  useEffect(() => {
    if (userStatus === 'unregistered') {
      setEmail('');
      setPassword('');
      setAdditionalInfo({ firstName: '', lastName: '', phone: '', grade: '', userType: 'I am a...' });
      setSubjects([]);
      setShowSubjectStep(false);
      setProfileCompletionError('');
    }
  }, [userStatus]);

  // Auto-trigger Google sign-in if coming from register page
  useEffect(() => {
    const urlParams = new URLSearchParams(location.search);
    const shouldGoogleSignIn = urlParams.get('googleSignIn');
    
    if (shouldGoogleSignIn === 'true' && userStatus === 'unregistered' && !isLoading) {
      // Clear the URL parameter to avoid re-triggering
      window.history.replaceState({}, document.title, location.pathname);
      handleGoogleSignIn();
    }
  }, [location.search, userStatus, isLoading]);
  const handleSubjectChange = (subject) => {
    setSubjects((prev) =>
      prev.includes(subject)
        ? prev.filter((s) => s !== subject)
        : [...prev, subject]
    );
  };
  const handleCompleteProfile = async (profileData, selectedSubjects = []) => {
    if (!currentUser) return;
    setIsLoading(true);
    setProfileCompletionError('');
    try {
      const updateData = {
        ...profileData,
        ...(profileData.userType === 'tutor' && { subjects: selectedSubjects }),
      };

      if (updateData.userType === 'I am a...') {
        setProfileCompletionError('Please select your role (Student, Parent, or Tutor).');
        setIsLoading(false);
        return;
      }
      if (!updateData.firstName?.trim()) {
        setProfileCompletionError('First Name is required.');
        setIsLoading(false);
        return;
      }
      if (updateData.userType === 'tutor' && selectedSubjects.length === 0) {
        setProfileCompletionError('Please select at least one subject you can teach.');
        setIsLoading(false);
        return;
      }

      // Check if user is trying to register as a tutor
      if (updateData.userType === 'tutor') {
        try {
          const tutorValidationResponse = await api.post('/api/validateTutorEmail', { email: currentUser.email });
          
          if (!tutorValidationResponse.ok) {
            const errorData = await tutorValidationResponse.json();
            setProfileCompletionError(errorData.message || 'This email is not authorized to register as a tutor. Please contact the administrator to request access.');
            setIsLoading(false);
            return;
          }
        } catch (validationError) {
          console.error('Error validating tutor email:', validationError);
          setProfileCompletionError('Error validating tutor registration. Please try again later.');
          setIsLoading(false);
          return;
        }
      }

      const response = await api.post('/api/completeProfile', { email: currentUser.email, updateData });

      const result = await response.json();      if (!response.ok) {
        throw new Error(result.message || 'Failed to complete profile.');
      }

      // Update user context with new status and data
      setUserDoc(result.docData);
      setUserStatus('verified'); // Set status to verified
      setStatusMessage('Profile completed successfully!');
      setShowSubjectStep(false);
      
      // If user is a tutor, redirect to Stripe onboarding
      if (result.docData?.userType === 'tutor') {
        setTimeout(() => {
          navigate('/stripe-connect-onboarding');
        }, 1000);
      }

    } catch (error) {
      console.error('Error completing profile:', error);
      setProfileCompletionError(`Profile completion failed: ${error.message}`);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSubmitAdditionalInfo = () => {
    if (additionalInfo.userType === 'I am a...') {
      setProfileCompletionError('Please select your role.');
      return;
    }
    if (!additionalInfo.firstName?.trim()) {
      setProfileCompletionError('First Name is required.');
      return;
    }
    if (additionalInfo.userType === 'tutor') {
      setShowSubjectStep(true);
      setProfileCompletionError('');
    } else {
      handleCompleteProfile(additionalInfo);
    }
  };

  const handleSubmitSubjects = () => {
    if (subjects.length === 0) {
      setProfileCompletionError("Please select at least one subject you can teach.");
      return;
    }
    handleCompleteProfile(additionalInfo, subjects);
  };

  const handleGoogleSignIn = async () => {
    try {
      await signInWithGoogle();
    } catch (error) {
      console.error('Google Sign-In error in Auth component:', error);
    }
  };

  const handleEmailPasswordSignIn = async (e) => {
    e.preventDefault();
    try {
      await signInWithEmailPassword(email, password);
    } catch (error) {
      console.error('Email/Password Sign-In error in Auth component:', error);
    }
  };

  const handleSignOut = async () => {
    try {
      await signOutUser();
    } catch (error) {
      console.error('Sign-out error:', error);
      setStatusMessage('Error signing out. Please try again.');
    }
  };
  const handleResendVerification = async () => {
    if (!resendVerificationEmail) {
      setStatusMessage('Unable to resend verification email. Please refresh the page and try again.');
      return;
    }
    
    try {
      const result = await resendVerificationEmail();
      
      if (result && result.status === 'sent') {
        setShowResendButton(false);
      } else if (result && result.status === 'already_verified') {
        setShowResendButton(false);
      }
      // Status message is already set by the resendVerificationEmail function
    } catch (error) {
      setStatusMessage(`Error resending email: ${error.message}`);
      console.error('Error resending verification:', error);
    }
  };

  const handlePasswordReset = async () => {
    if (!email) {
      setStatusMessage('Please enter your email address to reset the password.');
      return;
    }
    try {
      await sendPasswordResetEmail(auth, email);
      setStatusMessage('Password reset email sent. Please check your inbox.');
      setShowResetButton(false);
    } catch (error) {
      console.error('Error sending password reset:', error);
      setStatusMessage(`Failed to send password reset email: ${error.message}`);
    }
  };
  
  // Add effect to redirect verified tutors who haven't set up Stripe
  useEffect(() => {
    if (userStatus === 'verified' && userDoc?.userType === 'tutor' && !userDoc?.stripeConnectAccountId) {
      navigate('/stripe-connect-onboarding');
    }
  }, [userStatus, userDoc, navigate]);
  
  if (isLoading && userStatus === 'unregistered') {
    return (
      <div className="auth-page">
        <div className="auth-main-content auth-main-content-centered">
          <div className="auth-form-section">
            <div className="auth-loading">Loading...</div>
          </div>
        </div>
      </div>
    );
  }

  const renderContent = () => {
    switch (userStatus) {      case 'verified':
        return (
          <>
            <h2 className="auth-title">Welcome</h2>
            <div className="auth-user-info">
              <p className="auth-user-name">Signed in as: {currentUser?.email}</p>
              <button className="auth-signout-btn" onClick={handleSignOut}>
                Sign Out
              </button>
            </div>
          </>
        );      case 'pending_verification':
        return (
          <>
            <h2 className="auth-title">Verify Your Email</h2>
            <p className="auth-welcome-text">
              A verification link has been sent to <strong>{currentUser?.email}</strong>.
              Please check your inbox (and spam folder) and click the link to activate your account.
            </p>
            {statusMessage && (
              <div className="auth-status-message">
                <p>{statusMessage}</p>
              </div>
            )}
            {showResendButton && (
              <button
                className="auth-secondary-btn"
                onClick={handleResendVerification}
                disabled={isLoading}
              >
                {isLoading ? 'Sending...' : 'Resend Verification Email'}
              </button>
            )}
            <button className="auth-signout-btn" onClick={handleSignOut}>
              Sign Out
            </button>
          </>
        );      case 'pending_profile':
        return (
          <>
            {!showSubjectStep ? (
              <AdditionalInfoForm
                additionalInfo={additionalInfo}
                setAdditionalInfo={setAdditionalInfo}
                handleSubmitAdditionalInfo={handleSubmitAdditionalInfo}
                inputStyle="auth-input"
                signInButtonStyle="auth-submit-btn"
                isLoading={isLoading}
                errorMessage={profileCompletionError}
              />
            ) : (
              <div className="auth-subjects-container">
                <h3 className="auth-title">Select Subjects You Teach</h3>
                <p className="auth-welcome-text">Choose the subjects you are comfortable tutoring.</p>
                {profileCompletionError && <div className="auth-status-message"><p>{profileCompletionError}</p></div>}
                <div className="auth-subjects-grid">
                  {subjectOptions.map((subject) => (
                    <label 
                      key={subject} 
                      className={`auth-subject-label ${subjects.includes(subject) ? 'selected' : ''}`}
                    >
                      <input
                        type="checkbox"
                        checked={subjects.includes(subject)}
                        onChange={() => handleSubjectChange(subject)}
                      />
                      {subject.charAt(0).toUpperCase() + subject.slice(1)}
                    </label>
                  ))}
                </div>
                <div className="auth-button-group">
                  <button
                    type="button"
                    onClick={() => { setShowSubjectStep(false); setProfileCompletionError(''); }}
                    className="auth-back-btn"
                    disabled={isLoading}
                  >
                    Back
                  </button>
                  <button
                    type="button"
                    onClick={handleSubmitSubjects}
                    className="auth-submit-btn"
                    disabled={isLoading || subjects.length === 0}
                  >
                    {isLoading ? 'Saving...' : 'Complete Profile'}
                  </button>
                </div>
              </div>
            )}
            <button className="auth-signout-btn" onClick={handleSignOut}>
              Sign Out
            </button>
          </>
        );      case 'unregistered':
      default:
        return (
          <>
            <h2 className="auth-title">Sign In</h2>
            <p className="auth-subtitle">
              Don't have an account? <a href="/register">Register here</a>
            </p>
            <form className="auth-form" onSubmit={handleEmailPasswordSignIn}>
              <input
                className="auth-input"
                type="email"
                placeholder="Email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
              />
              <input
                className="auth-input"
                type="password"
                placeholder="Password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
              />
              <button 
                type="submit" 
                className="auth-submit-btn" 
                disabled={isLoading}
              >
                {isLoading ? 'Signing In...' : 'Sign In'}
              </button>
            </form>

            <div className="auth-divider">
              <hr /> <span>or</span> <hr />
            </div>

            {statusMessage && (
              <div className="auth-status-message">
                <p>{statusMessage}</p>
                {showResetButton && (
                  <button
                    className="auth-reset-btn"
                    onClick={handlePasswordReset}
                    disabled={isLoading}
                  >
                    Reset Password
                  </button>
                )}
                {statusMessage.includes("This email is already registered but not verified") && (
                  <button
                    className="auth-resend-btn"
                    onClick={handleResendVerification}
                    disabled={isLoading}
                  >
                    {isLoading ? 'Sending...' : 'Resend Verification Email'}
                  </button>
                )}
              </div>
            )}

            <button
              type="button"
              className="auth-google-btn"
              onClick={handleGoogleSignIn}
              disabled={isLoading}
            >
              <img src={googleLogo} alt="Google logo" className="google-logo-icon" />
              {isLoading ? 'Signing In...' : 'Continue with Google'}
            </button>
          </>
        );
    }
  };  return (
    <div className="auth-page">
      <div className="auth-main-content auth-main-content-centered">
        {/* Right side - Form */}
        <div className="auth-form-section">
          {renderContent()}
        </div>
      </div>
    </div>
  );
};

export default AuthPage;